import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { FormRenderer } from "components/form-renderer";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { Category } from "containers/Categories/categories";
import { Tag } from "containers/Tags/tags";
import { useAuth } from "contexts/auth-context";
import { useDictionaries } from "contexts/dictionaries-context";
import { useLoading } from "contexts/loading-context";
import { useProject } from "contexts/project-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import { DeviceFloppy, FileOff } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";
import { scrollOnError } from "utils/scrollOnError";

import { Post } from "../posts";
import { POSTS_FIELDS, PostsFormInputs } from "../posts.form";
import { POSTS_SHOW, POSTS_UPDATE } from "../posts.gql";

export default function PostsUpdate(): React.ReactElement {
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(
    false
  );
  const { enqueueSnackbar } = useSnackbar();
  const { setIsFetching, isLoading, setIsLoading } = useLoading();
  const { checkPermission } = useAuth();
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { activeProject, activeProjectId } = useProject();
  const { findValue } = useDictionaries();
  const [originalProjectId, setOriginalProjectId] = useState<number>();

  const {
    control,
    formState: { errors, isDirty, ...formState },
    handleSubmit,
    setValue,
    reset,
    watch,
    ...methods
  } = useForm<PostsFormInputs>();

  const { refetch, data, error: queryError } = useQuery(POSTS_SHOW, {
    variables: { id: id ? parseInt(id) : null },
  });
  const post: Post = data?.post;

  useEffect(() => {
    setIsFetching(true);
    if (data?.post) refetch();
  }, []);

  useEffect(() => {
    if (queryError?.graphQLErrors)
      enqueueSnackbar({
        message: (queryError as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [queryError]);

  useEffect(() => {
    async function setFile() {
      const response = await fetch(post?.imageUrl);
      const data = await response.blob();

      const extension = /\.(jpe?g|png|gif|svg)$/gi.exec(post?.imageUrl)?.[1];

      const metadata = {
        type: `image/${extension === "svg" ? "svg+xml" : "png"}`,
      };

      const file = new File([data], `${post?.slug}.${extension}`, metadata);

      setValue("image", file);

      setIsFetching(false);
    }

    if (post) {
      setOriginalProjectId(post?.project?.id);

      setValue("project", [{ ...post?.project, id: post?.project?.id }]);
      setValue("language", [
        {
          id: post?.language,
          label: (findValue(post?.language as string) as unknown) as string,
        },
      ]);
      setValue("name", post?.name);
      setValue("lead", post?.lead);
      setValue("isVisible", post?.isVisible);
      setValue("slug", post?.slug);
      setValue("content", post?.content);
      setValue("publishedAt", new Date(post?.publishedAt));
      setValue("tags", post?.tags);
      setValue("categories", post?.categories);
      setValue("metaTitle", post?.seoTagset?.metaTitle);
      setValue("metaDescription", post?.seoTagset?.metaDescription);
      setValue("ogTitle", post?.seoTagset?.ogTitle);
      setValue("ogDescription", post?.seoTagset?.ogDescription);
      setValue("metaKeywords", post?.seoTagset?.metaKeywords);
      setValue("canonical", post?.seoTagset?.canonical);
      !!post?.seoTagset?.robots &&
        setValue("robots", [
          {
            id: post?.seoTagset?.robots,
            label: post?.seoTagset?.robots,
          },
        ]);

      if (data?.post?.imageUrl) {
        setFile();
      } else {
        setIsFetching(false);
      }
    }
  }, [data]);

  useEffect(() => {
    !activeProject &&
      setValue("project", [{ ...post?.project, id: post?.project?.id }]);
  }, [activeProject]);

  const watchProject = watch("project");

  useEffect(() => {
    if (
      originalProjectId === watchProject?.[0]?.id ||
      originalProjectId === activeProjectId
    ) {
      setValue("language", [
        {
          id: post?.language,
          label: (findValue(post?.language as string) as unknown) as string,
        },
      ]);
      setValue("tags", post?.tags);
      setValue("categories", post?.categories);
    } else {
      setValue("language", []);
      setValue("tags", []);
      setValue("categories", []);
    }
  }, [watchProject, activeProject]);

  useEffect(() => {
    setIsFetching(true);
    if (data?.post) refetch();
  }, []);

  const [errorTimeStamp, setErrorTimeStamp] = useState<number>();

  const onError = (errors: Record<string, unknown>, e: any) =>
    setErrorTimeStamp(e.timeStamp);

  useEffect(() => {
    if (errors) scrollOnError(errors, POSTS_FIELDS, "update");
  }, [errorTimeStamp]);

  const [updatePost, { error }] = useMutation(POSTS_UPDATE);

  const onSubmit = async ({
    project,
    language,
    categories,
    isVisible,
    tags,
    metaTitle,
    metaDescription,
    metaKeywords,
    ogTitle,
    ogDescription,
    canonical,
    robots,
    ...values
  }: PostsFormInputs): Promise<void> => {
    setIsLoading(true);

    try {
      await updatePost({
        variables: {
          postUpdateInput: {
            id: id ? parseInt(id) : null,
            projectId: activeProjectId || project?.[0]?.id,
            language: language?.[0]?.id,
            isVisible: !!isVisible,
            categoryIds: categories?.map((category: Category) => category?.id),
            tagIds: tags?.map((tag: Tag) => tag?.id),
            ...values,
          },
          seoTagsetUpsertInput: {
            metaTitle,
            metaDescription,
            metaKeywords,
            ogTitle,
            ogDescription,
            canonical,
            robots: robots?.length ? robots?.[0]?.label : null,
          },
        },
      });

      enqueueSnackbar({
        message: "Zapisano pomyślnie",
        variant: "success",
      });
      history.push(`/posts/${id}`);
    } catch (error: unknown) {
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.post.update))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title={post?.name}
        labels={["Aktualności", "Edytowanie"]}
        goBackOption
        buttons={[
          {
            label: "Anuluj",
            kind: KIND.secondary,
            startEnhancer: <FileOff size={18} />,
            disabled: isLoading,
            onClick: () =>
              isDirty ? setIsCancelConfirmDialogOpen(true) : history.goBack(),
          },
          {
            label: "Zapisz",
            kind: KIND.primary,
            startEnhancer: <DeviceFloppy size={18} />,
            onClick: handleSubmit(onSubmit, onError),
            disabled: isLoading,
            isLoading: isLoading,
            type: "submit",
            formId: "updatePost",
          },
        ]}
      />
      <Content>
        <FormProvider<PostsFormInputs>
          control={control}
          formState={{ errors, isDirty, ...formState }}
          handleSubmit={handleSubmit}
          reset={reset}
          setValue={setValue}
          watch={watch}
          {...methods}
        >
          <FormRenderer<PostsFormInputs>
            id="updatePost"
            onSubmit={handleSubmit(onSubmit, onError)}
            type="update"
            isLoading={isLoading}
            error={error}
            fields={POSTS_FIELDS}
          />
        </FormProvider>
        <ConfirmDialog
          isOpen={isCancelConfirmDialogOpen}
          label="Anulowanie edycji aktualności"
          close={() => setIsCancelConfirmDialogOpen(false)}
          confirm={() => history.goBack()}
        />
      </Content>
    </article>
  );
}
